if (!customElements.get('kiwi-product-tabs')) {
  class KiwiProductTabs extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.tabs = Array.from(this.querySelectorAll('[data-kiwi-product-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-kiwi-product-panel]'));
      this.tabLinks = Array.from(this.querySelectorAll('[data-kiwi-product-tab-link]'));
      this.activeTab = null;
      this.boundOnClick = this.onClick.bind(this);
      this.boundOnResize = this.onResize.bind(this);

      if (!this.tabs.length || !this.panels.length) return;

      this.addEventListener('click', this.boundOnClick);
      window.addEventListener('resize', this.boundOnResize);

      this.scrollers = this.panels.map((panel) => {
        const scroller = panel.querySelector('[data-kiwi-product-scroller]');
        const prevButton = panel.querySelector('[data-kiwi-product-prev]');
        const nextButton = panel.querySelector('[data-kiwi-product-next]');
        const onScroll = () => this.updateArrow(panel);

        scroller?.addEventListener('scroll', onScroll, { passive: true });

        return {
          panel,
          scroller,
          prevButton,
          nextButton,
          onScroll,
        };
      });

      const activeTab = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || this.tabs[0];
      if (activeTab) {
        this.activateTab(activeTab, { focus: false, animate: false });
      } else {
        this.updateArrows();
      }
    }

    disconnectedCallback() {
      // Section replacement/reconnection must rebind once, without retaining an old animation timer.
      this.initialized = false;
      window.clearTimeout(this.panelAnimationTimer);
      this.removeEventListener('click', this.boundOnClick);
      window.removeEventListener('resize', this.boundOnResize);

      this.scrollers?.forEach(({ scroller, onScroll }) => {
        scroller?.removeEventListener('scroll', onScroll);
      });
    }

    onClick(event) {
      const tab = event.target.closest('[data-kiwi-product-tab]');
      if (tab && this.contains(tab)) {
        event.preventDefault();
        const previousTab = this.activeTab;
        this.activateTab(tab);
        if (event.isTrusted) this.trackPlanChange(previousTab, tab, event);
        return;
      }

      const nextButton = event.target.closest('[data-kiwi-product-next]');
      if (nextButton && this.contains(nextButton)) {
        event.preventDefault();
        this.scrollNext(nextButton);
        return;
      }

      const prevButton = event.target.closest('[data-kiwi-product-prev]');
      if (prevButton && this.contains(prevButton)) {
        event.preventDefault();
        this.scrollPrev(prevButton);
      }
    }

    trackPlanChange(previousTab, tab, event) {
      // Only user-driven, completed switches belong to this event; initialization and arrows do not.
      try {
        if (this.dataset.trackingPage !== 'home' || !previousTab || previousTab === tab || this.activeTab !== tab) return;
        const panel = this.panels.find((item) => item.id === tab.getAttribute('aria-controls'));
        if (!panel || panel.hidden || !this.contains(panel)) return;
        const tracking = window.KiwibitTracking;
        if (typeof tracking?.track !== 'function') return;
        const result = tracking.track('kwb_home_plan_select', {
          ...tracking.semanticFields?.(tab),
          pageType: this.dataset.trackingPage,
          locale: this.dataset.trackingLocale || 'und',
          sectionId: this.dataset.sectionId,
          previousPlan: previousTab.dataset.trackingPlan,
          selectedPlan: tab.dataset.trackingPlan,
          tabPosition: this.tabs.indexOf(tab) + 1,
        });
        window.KiwibitSitewideInteractions?.claimLegacyAction(event, result);
      } catch (_) {
        // A missing/broken analytics runtime must not break product browsing.
      }
    }

    onResize() {
      this.updateArrows();
    }

    activateTab(tab, options = {}) {
      const { focus = true, animate = true } = options;
      const panelId = tab.getAttribute('aria-controls');
      const previousTab = this.activeTab;
      const previousPanelId = previousTab?.getAttribute('aria-controls');
      const previousPanel = previousPanelId ? document.getElementById(previousPanelId) : null;
      const nextPanel = panelId ? document.getElementById(panelId) : null;
      const previousIndex = previousTab ? this.tabs.indexOf(previousTab) : -1;
      const nextIndex = this.tabs.indexOf(tab);
      const directionClass = previousIndex <= nextIndex ? 'is-entering-from-right' : 'is-entering-from-left';

      this.tabs.forEach((item) => {
        const isActive = item === tab;
        item.classList.toggle('is-active', isActive);
        item.setAttribute('aria-selected', isActive ? 'true' : 'false');
        item.setAttribute('tabindex', isActive ? '0' : '-1');
      });

      this.panels.forEach((panel) => {
        panel.classList.remove('is-entering-from-right', 'is-entering-from-left');
        const isActive = panel.id === panelId;
        panel.classList.toggle('is-active', isActive);
        panel.hidden = !isActive;
      });

      if (nextPanel) {
        nextPanel.hidden = false;
        nextPanel.classList.add('is-active');

        if (animate && previousPanel !== nextPanel) {
          nextPanel.offsetWidth;
          nextPanel.classList.add(directionClass);
          window.clearTimeout(this.panelAnimationTimer);
          this.panelAnimationTimer = window.setTimeout(() => {
            nextPanel.classList.remove('is-entering-from-right', 'is-entering-from-left');
          }, 380);
        }
      }

      if (focus) {
        tab.focus({ preventScroll: true });
      }

      this.activeTab = tab;
      this.updateTabLinks(tab);
      this.updateArrows();
    }

    scrollNext(nextButton) {
      const entry = this.scrollers?.find((item) => item.nextButton === nextButton);
      if (!entry?.scroller) return;

      const card = entry.scroller.querySelector('.kiwi-product-tabs__card');
      const computedStyle = window.getComputedStyle(entry.scroller);
      const gapValue = computedStyle.columnGap || computedStyle.gap || '0';
      const gap = Number.parseFloat(gapValue) || 0;
      const distance = (card?.getBoundingClientRect().width || entry.scroller.clientWidth * 0.85) + gap;

      entry.scroller.scrollBy({
        left: distance,
        behavior: 'smooth',
      });
    }

    scrollPrev(prevButton) {
      const entry = this.scrollers?.find((item) => item.prevButton === prevButton);
      if (!entry?.scroller) return;

      const card = entry.scroller.querySelector('.kiwi-product-tabs__card');
      const computedStyle = window.getComputedStyle(entry.scroller);
      const gapValue = computedStyle.columnGap || computedStyle.gap || '0';
      const gap = Number.parseFloat(gapValue) || 0;
      const distance = (card?.getBoundingClientRect().width || entry.scroller.clientWidth * 0.85) + gap;

      entry.scroller.scrollBy({
        left: -distance,
        behavior: 'smooth',
      });
    }

    updateArrows() {
      this.panels.forEach((panel) => this.updateArrow(panel));
    }

    updateTabLinks(tab) {
      if (!this.tabLinks?.length) return;

      const link = tab?.dataset?.kiwiProductCtaLink || '';
      const label = tab?.dataset?.kiwiProductCtaLabel || '';

      this.tabLinks.forEach((tabLink) => {
        const labelNode = tabLink.querySelector('[data-kiwi-product-tab-link-label]');
        const hasContent = Boolean(link && label);

        tabLink.hidden = !hasContent;

        if (!hasContent) {
          tabLink.removeAttribute('href');
          if (labelNode) labelNode.textContent = '';
          return;
        }

        tabLink.setAttribute('href', link);

        if (labelNode) {
          labelNode.textContent = label;
        }
      });
    }

    updateArrow(panel) {
      const entry = this.scrollers?.find((item) => item.panel === panel);
      if (!entry?.nextButton || !entry.scroller) return;

      if (window.matchMedia('(max-width: 989.98px)').matches || panel.hidden) {
        if (entry.prevButton) entry.prevButton.hidden = true;
        entry.nextButton.hidden = true;
        return;
      }

      const maxScrollLeft = Math.max(0, entry.scroller.scrollWidth - entry.scroller.clientWidth);
      const canScroll = maxScrollLeft > 4;
      const hasMoved = entry.scroller.scrollLeft > 4;
      const isAtEnd = entry.scroller.scrollLeft >= maxScrollLeft - 4;

      if (entry.prevButton) {
        entry.prevButton.hidden = !canScroll || !hasMoved;
      }
      entry.nextButton.hidden = !canScroll || isAtEnd;
    }
  }

  customElements.define('kiwi-product-tabs', KiwiProductTabs);
}
